import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { Input } from '../../components/ui/input';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { TeamRegistrationService } from './TeamRegistrationService';
import { RegistrationState, TeamRow } from './TeamRegistration.types';
import { useCountdownClock } from './useCountdownClock';
import { useTeamRegistrationStore } from '../../store/teamRegistrationStore';
import { useReportStore } from '../../store/reportStore';

const TABLE_HEADER: Array<{ key: string; text: string }> = [
  { key: 'row', text: '🔢 Nr.' },
  { key: 'title', text: '📋 Titel' },
  { key: 'deadline', text: '⏰ Meldeschluss' },
  { key: 'status', text: '📊 Status' },
  { key: 'link', text: '🔗 Registrierungslink' },
];

/** One colour per registration state, so "not open yet" does not read as "over". */
const STATUS_BADGE: Record<RegistrationState, string> = {
  open: 'text-green-600 border-green-200 bg-green-50',
  not_yet_open: 'text-amber-700 border-amber-200 bg-amber-50',
  closed: 'text-gray-600 border-gray-200 bg-gray-50',
};

const ListTab: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const navigate = useNavigate();
  const [teamData, setTeamData] = useState<TeamRow[]>([]);
  const setTitle = useTeamRegistrationStore((state) => state.setTitle);
  const resetTeamRegistration = useTeamRegistrationStore(
    (state) => state.reset,
  );
  const resetReport = useReportStore((state) => state.reset);
  useEffect(() => {
    const fetchReportList = async () => {
      try {
        setTeamData(await TeamRegistrationService.fetchReportList());
      } catch (error) {
        console.error('Error fetching report list:', error);
      }
    };
    fetchReportList();
  }, []);

  // Filter data based on search term and date range
  const filteredData = useMemo(() => {
    const start = startDate ? new Date(startDate) : null;
    const end = endDate ? new Date(endDate) : null;

    return teamData.filter((team) => {
      const titleMatches = team.title
        .toLowerCase()
        .includes(searchTerm.toLowerCase());
      if (!titleMatches) return false;

      const teamDate = TeamRegistrationService.parseDottedDateToDate(
        team.deadline,
      );
      if (!start && !end) return true;
      if (!teamDate) return false;

      if (start && end) return teamDate >= start && teamDate <= end;
      if (start) return teamDate >= start;
      if (end) return teamDate <= end;
      return true;
    });
  }, [teamData, searchTerm, startDate, endDate]);

  const handleRegistrationClick = (teamId: number, title: string) => {
    setTitle(title);
    // Reset both stores before starting new registration
    resetTeamRegistration();
    resetReport();
    navigate(`/team-registration/${teamId}/bank`);
  };

  // One clock for the table; every row's countdown and state reads off it, so
  // a report that opens while the list is on screen becomes registrable
  // without a reload.
  const now = useCountdownClock(teamData);

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <div className="font-medium text-lg">Jahresmeldungen</div>
        <div className="text-sm text-gray-500 font-medium">
          Gesamt: {filteredData.length}
        </div>
      </div>

      <div className="flex flex-col lg:flex-row gap-4 mb-6">
        <div className="relative flex-1">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            type="text"
            placeholder="Nach Titel suchen..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>

        <div className="flex flex-col sm:flex-row gap-2">
          <div className="flex items-center gap-2 flex-1">
            <span className="text-sm text-gray-600">Von:</span>
            <Input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-full"
            />
          </div>
          <div className="flex items-center gap-2 flex-1">
            <span className="text-sm text-gray-600">Bis:</span>
            <Input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="w-full"
            />
          </div>
        </div>
      </div>

      <Table>
        <TableCaption>Team-Anmeldungsübersicht</TableCaption>
        <TableHeader>
          <TableRow>
            {TABLE_HEADER.map((header) => (
              <TableHead className="whitespace-nowrap" key={header.key}>
                {header.text}
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {filteredData.map((team, index) => {
            const state = TeamRegistrationService.registrationCountdown(
              team,
              now,
            ).state;
            const canRegister = state === 'open';
            return (
              <TableRow key={index}>
                <TableCell>
                  <div className="font-medium">{team.id}</div>
                </TableCell>
                <TableCell>
                  <div className="font-bold">{team.title}</div>
                </TableCell>
                <TableCell>
                  <div className="text-sm">{team.deadline}</div>
                  <div className="text-xs text-gray-500 tabular-nums">
                    {TeamRegistrationService.registrationHint(team, now)}
                  </div>
                </TableCell>
                <TableCell>
                  <div
                    className={`inline-flex whitespace-nowrap items-center text-xs font-medium border rounded-full px-2 py-1 ${STATUS_BADGE[state]}`}
                  >
                    {TeamRegistrationService.registrationStatusLabel(state)}
                  </div>
                </TableCell>
                <TableCell>
                  <div
                    className={`${canRegister ? 'text-[#52A445] hover:underline cursor-pointer inline-block w-[120px] font-bold py-0.5' : 'text-gray-400 cursor-default'}`}
                    onClick={() =>
                      canRegister &&
                      handleRegistrationClick(team.id, team.title)
                    }
                  >
                    {canRegister ? 'Jetzt registrieren' : 'Abgeschlossen'}
                  </div>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
};

export default ListTab;
